import { useState } from 'react';
import { Clock } from 'lucide-react';
import clsx from 'clsx';
import { NDTDashboard } from '../../../ndt/NDTAnalytics';
import { DashboardProject } from '../../types/project';

type AnalyticsView = 'ndt' | 'weld' | 'combined' | 'executive';

type ViewEntry = {
  id: AnalyticsView;
  label: string;
  comingSoon?: boolean;
};

const VIEWS: ViewEntry[] = [
  { id: 'ndt', label: 'NDT Analytics' },
  { id: 'weld', label: 'Weld Analytics', comingSoon: true },
  { id: 'combined', label: 'Combined', comingSoon: true },
  { id: 'executive', label: 'Executive', comingSoon: true },
];

const COMING_SOON_COPY: Record<Exclude<AnalyticsView, 'ndt'>, string> = {
  weld: 'Welder performance, shift comparison, output trends, and individual welder analysis.',
  combined: 'Cross-cutting views: welder × defect correlation, shift × defect, predictive risk, and root-cause.',
  executive: 'High-level dashboard pulling headline KPIs from NDT, Welding, and combined signals.',
};

const ComingSoon = ({ title, description }: { title: string; description: string }) => (
  <div className="rounded-2xl border border-dashed border-[#E2E5EA] bg-white/60 p-12 text-center">
    <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(230,161,0,0.08)] text-[#B38600]">
      <Clock size={22} />
    </div>
    <h3 className="font-heading text-lg font-semibold text-blueprint-blue">{title} — Coming soon</h3>
    <p className="mx-auto mt-2 max-w-md text-sm text-heritage-grey">{description}</p>
  </div>
);

export const AnalyticsShell = ({ project }: { project: DashboardProject }) => {
  const [activeView, setActiveView] = useState<AnalyticsView>('ndt');

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          {/* <h2 className="font-heading text-xl font-semibold text-blueprint-blue">Analytics</h2>
          <p className="text-sm text-heritage-grey">
            Executive overview and detailed analytics across NDT, Welding, and combined signals.
          </p> */}
        </div>

        <div
          role="tablist"
          aria-label="Analytics view"
          className="inline-flex self-start rounded-lg border border-[#E2E5EA] bg-page-bg p-[3px]"
        >
          {VIEWS.map((view) => {
            const isActive = activeView === view.id;
            return (
              <button
                key={view.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveView(view.id)}
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-[13px] transition-all duration-150',
                  isActive
                    ? 'bg-white font-semibold text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                    : 'font-medium text-heritage-grey hover:text-blueprint-blue',
                )}
              >
                {view.label}
                {view.comingSoon && (
                  <span className="rounded-full bg-[rgba(230,161,0,0.08)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#B38600]">
                    Soon
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {activeView === 'ndt' && <NDTDashboard project={project} />}
      {activeView === 'weld' && <ComingSoon title="Weld Analytics" description={COMING_SOON_COPY.weld} />}
      {activeView === 'combined' && <ComingSoon title="Combined Analytics" description={COMING_SOON_COPY.combined} />}
      {activeView === 'executive' && <ComingSoon title="Executive Summary" description={COMING_SOON_COPY.executive} />}
    </div>
  );
};
